import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ArrowLeftSLineIcon from "remixicon-react/ArrowLeftSLineIcon";
import { getLayout, setLayout, updateLayout } from "../../../core/actionCreators/lmsActionCreator";
import { PLATFORMS } from "../../../utils/constants";
import Header from "../../components/Header/Header";
import Loader from "../../components/Loader/Loader";
import SideNavbar from "../../components/Navbar/SideNavbar";
import "./EditLayoutPage.scss";

const EditLayoutPage = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()
    let { user } = useSelector((s: any) => s.authStore);
    let { isLoading } = useSelector((s: any) => s.lmsStore);

    //data.cmsLayoutResponse
    const [layoutDetail, setLayoutDetail] = useState<any>(null)
    const [layoutName, setLayoutName] = useState("")
    const [device, setDevice] = useState("")
    const [status, setStatus] = useState(true)

    //data.attributes & data.components
    const [attributes, setAttributes] = useState<any[]>([])
    const [components, setComponents] = useState<any[]>([])

    //createdBy sits on data, not on data.cmsLayoutResponse
    const [createdBy, setCreatedBy] = useState<any>(null)

    //Get Layout By Id
    useEffect(() => {
        if (!params.layoutId) return
        let callback = (r: any, _data: any) => {
            if (r || !_data || !_data.cmsLayoutResponse) return
            setLayoutDetail(_data.cmsLayoutResponse)
            setLayoutName(_data.cmsLayoutResponse.layoutName || "")
            setDevice(_data.cmsLayoutResponse.device || "")
            setStatus(_data.cmsLayoutResponse.status)
            setAttributes(_data.attributes || [])
            setComponents(_data.components || [])
            setCreatedBy(_data.createdBy)
        }
        dispatch(getLayout(params.layoutId, callback))
        return () => {
            dispatch(setLayout({}))
        }
    }, [params])

    //Keep the saved device value selectable even when its casing does not match PLATFORMS
    const platformOptions = () => {
        if (!device) return PLATFORMS
        let isKnown = PLATFORMS.some((p) => p.value.toLowerCase() == device.toLowerCase())
        return isKnown ? PLATFORMS : [{ "title": device, "value": device }, ...PLATFORMS]
    }

    const selectedDevice = () => {
        let matched = PLATFORMS.find((p) => p.value.toLowerCase() == device.toLowerCase())
        return matched ? matched.value : device
    }

    const attributeChangeHandler = (_index: number, _value: any) => {
        setAttributes(attributes.map((a: any, index: number) =>
            index == _index ? { ...a, "attributeValue": _value } : a
        ))
    }

    const componentChangeHandler = (_index: number, _key: string, _value: any) => {
        setComponents(components.map((c: any, index: number) =>
            index == _index ? { ...c, [_key]: _value } : c
        ))
    }

    const backHandler = () => {
        navigate("/layout")
    }

    //Update Layout
    const updateLayoutHandler = () => {
        if (!device || !layoutName) {
            alert("Fields are empty.")
            return;
        }

        let requestPayload = {
            "layout": {
                ...layoutDetail,
                "layoutName": layoutName,
                "device": device,
                "status": status
            },
            "attributes": attributes,
            "components": components.map((c: any) => ({ ...c, "order": Number(c.order) })),
            "modifiedBy": user.id || -1
        }

        let callback = (r: any) => {
            //Back to the listing once saved
            if (!r) {
                navigate("/layout")
                return
            }
        }
        dispatch(updateLayout(requestPayload, callback))
    }

    const formatDate = (_date: any) => _date ? new Date(_date).toLocaleString() : "N/A"

    return (<>
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container edit-layout-page bg-sky-50">
                    <div className="Subheader bg-white p-[15px] shadow-[0_2px_6px_rgba(0,0,0,.1)] flex items-center justify-between">
                        <div className="flex items-center">
                            <ArrowLeftSLineIcon onClick={backHandler} size={24} className="mr-4 cursor-pointer" />
                            <p className="text-xl font-bold text-[#43425D] flex items-center">Update Page</p>
                        </div>
                        <div className="flex items-center">
                            <button onClick={backHandler} className="bg-transparent font-semibold text-slate-800 py-2 px-6">
                                Cancel
                            </button>
                            <button disabled={!layoutDetail} onClick={updateLayoutHandler} className="py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-50">
                                Update
                            </button>
                        </div>
                    </div>

                    {isLoading && !layoutDetail ? <Loader /> : <>
                        {layoutDetail ? <div className="p-[15px]">

                            <div className="section-card">
                                <p className="section-title">Layout Details</p>
                                <div className="flex flex-wrap gap-[20px] items-start">
                                    <div className="form-field">
                                        <label>Layout Name</label>
                                        <input value={layoutName} onChange={(e) => setLayoutName(e.target.value)} type="text" placeholder="Enter Page Name" />
                                    </div>
                                    <div className="form-field">
                                        <label>Platform</label>
                                        <select value={selectedDevice()} onChange={(e) => setDevice(e.target.value)}>
                                            {platformOptions().map((d, index) =>
                                                <option key={index} value={d.value}>{d.title}</option>
                                            )}
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label>Status</label>
                                        <select value={status ? "true" : "false"} onChange={(e) => setStatus(e.target.value == "true")}>
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-[40px] items-start mt-[20px]">
                                    <div className="read-only-field">
                                        <label>ID</label>
                                        <span>{layoutDetail.id}</span>
                                    </div>
                                    <div className="read-only-field">
                                        <label>Layout Code</label>
                                        <span>{layoutDetail.layoutCode}</span>
                                    </div>
                                    <div className="read-only-field">
                                        <label>Parent Structure</label>
                                        <span>{String(layoutDetail.isParentStructure)}</span>
                                    </div>
                                    <div className="read-only-field">
                                        <label>Created by</label>
                                        <span>{createdBy || layoutDetail.createdBy || "N/A"}</span>
                                    </div>
                                    <div className="read-only-field">
                                        <label>Created At</label>
                                        <span>{formatDate(layoutDetail.createdAt)}</span>
                                    </div>
                                    <div className="read-only-field">
                                        <label>Updated At</label>
                                        <span>{formatDate(layoutDetail.modifiedAt)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="section-card">
                                <p className="section-title">Attributes ({attributes.length})</p>
                                {attributes.length ?
                                    <div className="overflow-x-auto">
                                        <table className="detail-tbl">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Attribute Key ID</th>
                                                    <th>Attribute Value</th>
                                                    <th>Created At</th>
                                                    <th>Updated At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {attributes.map((a: any, index: number) =>
                                                    <tr key={a.id || index}>
                                                        <td>{a.id}</td>
                                                        <td>{a.attributeKeyId}</td>
                                                        <td className="value-cell">
                                                            <input
                                                                className="attribute-value"
                                                                type="text"
                                                                value={a.attributeValue}
                                                                onChange={(e) => attributeChangeHandler(index, e.target.value)} />
                                                        </td>
                                                        <td>{formatDate(a.createdAt)}</td>
                                                        <td>{formatDate(a.modifiedAt)}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    : <p className="text-xs text-[#959595]">No attributes for this page.</p>}
                            </div>

                            <div className="section-card">
                                <p className="section-title">Components ({components.length})</p>
                                {components.length ?
                                    <div className="overflow-x-auto">
                                        <table className="detail-tbl">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Component ID</th>
                                                    <th>Component Name</th>
                                                    <th>Order</th>
                                                    <th>Status</th>
                                                    <th>Created At</th>
                                                    <th>Updated At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {components.map((c: any, index: number) =>
                                                    <tr key={c.id || index}>
                                                        <td>{c.id}</td>
                                                        <td>{c.componentId}</td>
                                                        <td className="value-cell">
                                                            <input
                                                                className="component-name"
                                                                type="text"
                                                                value={c.componentName}
                                                                onChange={(e) => componentChangeHandler(index, "componentName", e.target.value)} />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={c.order}
                                                                onChange={(e) => componentChangeHandler(index, "order", e.target.value)} />
                                                        </td>
                                                        <td>
                                                            <select
                                                                value={c.status ? "true" : "false"}
                                                                onChange={(e) => componentChangeHandler(index, "status", e.target.value == "true")}>
                                                                <option value="true">Active</option>
                                                                <option value="false">Inactive</option>
                                                            </select>
                                                        </td>
                                                        <td>{formatDate(c.createdAt)}</td>
                                                        <td>{formatDate(c.modifiedAt)}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    : <p className="text-xs text-[#959595]">No components for this page.</p>}
                            </div>

                        </div> : null}
                    </>}
                </div>
            </div>
        </div>
    </>)
}

export default EditLayoutPage
